import type { ModuleDeclaration, Module, BindingSpecifier } from './esm';

export function renderESM(decl: ModuleDeclaration) {
  if (decl.type === 'classExport') {
    return `export class ${decl.ident}`;
  } else if (decl.type === 'functionExport') {
    return `export ${decl.async ? 'async ' : ''}function${decl.generator ? '*' : ''} ${decl.ident}`;
  }

  const output = [];

  output.push(decl.type);

  if (decl.type === 'import') {
    if (decl.dynamic) {
      return `import(${renderModule(decl.module)})`;
    }

    if (decl.namedImports) {
      if (decl.defaultIdent) {
        output.push(`${decl.defaultIdent},`);
      }

      output.push(...renderNamed(decl.namedImports), 'from');
    } else if (decl.namespaceIdent) {
      output.push('* as', decl.namespaceIdent, 'from');
    } else if (decl.defaultIdent) {
      output.push(decl.defaultIdent, 'from');
    }

    output.push(renderModule(decl.module));
  } else if (decl.type === 'export') {
    if (decl.immutable) {
      throw new Error('unreachable: immutable export cannot be rendered');
    }

    if (decl.namedExports) {
      output.push(...renderNamed(decl.namedExports));
    }

    if (decl.namespaceIdent) {
      output.push('* as', decl.namespaceIdent);
    }

    if (decl.module) {
      if (!decl.namedExports && !decl.namespaceIdent) {
        output.push('*');
      }

      output.push('from', renderModule(decl.module));
    }
  }

  return output.join(' ');
}

function renderModule(mod: Module) {
  const doubleQuoted = JSON.stringify(mod.path);

  if (mod.quote === '"') {
    return doubleQuoted;
  }

  // relying on JSON.stringify to handle non-quote escaping will be more robust than trying to roll our own, so we just reuse it

  return (
    "'" +
    doubleQuoted
      // strip the quotes
      .slice(1, -1)
      // unescape doubles
      .replace(/\\"/g, '"')
      // escape singles
      .replace(/'/g, "\\'") +
    "'"
  );
}

function renderNamed(named: BindingSpecifier[]) {
  const output = ['{'];

  const list = named.map(({ specifier, alias }) => {
    if (!alias) {
      return specifier;
    }

    return `${specifier} as ${alias}`;
  });

  output.push(sepBy(list, ', ').join(''));

  output.push('}');

  return output;
}

function sepBy<T, U>(arr: T[], sep: U): (T | U)[] {
  return arr.reduce((acc: (T | U)[], cur: T, i: number) => {
    if (i === 0) {
      return [cur];
    }

    return [...acc, sep, cur];
  }, []);
}
